import { Dispatch, SetStateAction, useState, ChangeEvent } from 'react'
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'

import { UpdateTranslationModal } from '../update-translation-form'
import { ETranslationNs, ITranslationWithDefault } from 'src/types'

import { DEFAULT_LANGUAGE_CODE } from '../constants'
import { useStyles } from './styles'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { useUpdateTranslationSwitch } from 'src/hooks/languages/use-update-translation-switch.hook'
import { TableItemWithPopover } from './table-item-with-popover'

interface IProps {
  items?: void | ITranslationWithDefault[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
  languageCode: string
  isIndeterminated: boolean
  isAllChecked: boolean
  isItemSelected: (id: string) => boolean
  handleItemCheck: (id: string) => (e: ChangeEvent<HTMLInputElement>) => void
  handleAllItemsCheck: (event: ChangeEvent<HTMLInputElement>) => void
}

const TranslationsTable = ({
  items = [],
  sort,
  setSort,
  languageCode,
  isIndeterminated,
  isAllChecked,
  handleItemCheck,
  isItemSelected,
  handleAllItemsCheck,
}: IProps) => {
  const [updateValues, setUpdateValues] = useState<ITranslationWithDefault | null>(null)
  const theme = useTheme()
  const classes = useStyles()
  const { mutateAsync: updateTranslationSwitch } = useUpdateTranslationSwitch()

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleUpdate = (item: ITranslationWithDefault) => () => {
    setUpdateValues(item)
  }

  const handleClose = () => {
    setUpdateValues(null)
  }

  const handleSwitchClick = (item: ITranslationWithDefault) => {
    updateTranslationSwitch({
      translationId: item.id,
      helper_text_update: !item.helper_text_update,
    })
  }

  const isDefaultLang = languageCode === DEFAULT_LANGUAGE_CODE
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ 'td, th': { border: 0 } }}>
              <TableCell align="center">
                <Checkbox
                  indeterminate={isIndeterminated}
                  sx={{ color: theme.palette.custom.grey2 }}
                  onChange={handleAllItemsCheck}
                  checked={isAllChecked}
                />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('language')}
                  active={sort.split('-')[0] === 'language'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="translations-code-filter" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('namespace')}
                  active={sort.split('-')[0] === 'namespace'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="impact-periods-name-column" />
                </TableSortLabel>
              </TableCell>
              {!isDefaultLang && (
                <TableCell align="center">
                  <HelperWrapper
                    t="translations-table-default-translation-column"
                    ns={ETranslationNs.Common}
                  />
                </TableCell>
              )}
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('value')}
                  active={sort.split('-')[0] === 'value'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper
                    t="translations-table-translation-column"
                    ns={ETranslationNs.Common}
                  />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('helper_text')}
                  active={sort.split('-')[0] === 'helper_text'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="helper-text-input-label" ns={ETranslationNs.Common} />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('type')}
                  active={sort.split('-')[0] === 'type'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="type-translation-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('helper_text_update')}
                  active={sort.split('-')[0] === 'helper_text_update'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="helper-text-input-label" ns={ETranslationNs.Common} />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items?.map((item) => (
              <TableRow
                key={item.id}
                sx={{ 'td, th': { border: 0 } }}
                style={{
                  borderTop: `1px solid ${theme.palette.custom.grey}`,
                  borderBottom: `1px solid ${theme.palette.custom.grey}`,
                }}
              >
                <TableCell align="center">
                  {item.language.code !== DEFAULT_LANGUAGE_CODE && (
                    <Checkbox
                      checked={isItemSelected(item.id)}
                      onChange={handleItemCheck(item.id)}
                      sx={{ color: theme.palette.custom.grey2 }}
                      disabled={item.language.code === DEFAULT_LANGUAGE_CODE}
                      // onClick={() => setSelectedItemsIds((prevIds) => [...prevIds, item.id])}
                    />
                  )}
                </TableCell>
                <TableCell align="center">
                  {`${item.language.name} (${item.language.code.toUpperCase()})`}
                </TableCell>
                <TableCell
                  align="center"
                  className={
                    item.review_required
                      ? classes.translationReview
                      : !item.value
                      ? classes.translationMissing
                      : undefined
                  }
                >
                  {item.namespace}
                </TableCell>
                {!isDefaultLang && <TableCell width="25%">{item.default?.value}</TableCell>}

                {item.language.code === DEFAULT_LANGUAGE_CODE ? (
                  <TableCell width="25%" align="center">
                    {item.value || '-'}
                  </TableCell>
                ) : (
                  <TableItemWithPopover
                    cellValue={item.value || ''}
                    translation={item.value || ''}
                    originalTranslation={item.default?.value || ''}
                    aiTranslation={item.ai_translation || ''}
                    helperTranslation={item.helper_text || ''}
                    helperOriginalTranslation={item.default?.helper_text || ''}
                    helperAiTranslation={item.ai_translation_helper || ''}
                  />
                )}
                {item.language.code === DEFAULT_LANGUAGE_CODE ? (
                  <TableCell width="25%" align="center">
                    {item.helper_text}
                  </TableCell>
                ) : (
                  <TableItemWithPopover
                    cellValue={item.helper_text || ''}
                    translation={item.value || ''}
                    originalTranslation={item.default?.value || ''}
                    aiTranslation={item.ai_translation || ''}
                    helperTranslation={item.helper_text || ''}
                    helperOriginalTranslation={item.default?.helper_text || ''}
                    helperAiTranslation={item.ai_translation_helper || ''}
                  />
                )}
                <TableCell width="15%" align="center">
                  {item.type}
                </TableCell>
                <TableCell width="5%" align="center">
                  <Checkbox
                    checked={item.helper_text_update}
                    onChange={() => handleSwitchClick(item)}
                  />
                </TableCell>
                <TableCell align="center" width="5%">
                  <Button onClick={handleUpdate(item)}>
                    <HelperWrapper t="update-button-text" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UpdateTranslationModal
        lng={languageCode}
        initialValues={updateValues}
        open={!!updateValues}
        handleClose={handleClose}
      />
    </>
  )
}

export { TranslationsTable }
